import { useSendDatahubDefaultEvents } from 'src/hook/useSendDatahubDefaultEvents'
import { Action, Entity, FailureDescription, TryResult } from 'src/utils/datahubDefaultEvent'
import { CommunityMemberPermissionsGroup } from 'src/services/community'
import { ArrivedFrom } from 'src/utils/datahub/enums'
import { useNewFeed } from '../useNewFeed'
import {
  SendChosenMentionEventParams,
  SendMentionResultsEventParams,
  SendPublishContentMentionEventParams
} from './useMentionTrackEvents.types'

const useMentionTrackEvents = () => {
  const { dispatchDatahubDefaultEvents } = useSendDatahubDefaultEvents()
  const { enabled: newFeedEnabled, optimizely } = useNewFeed()

  const sendMentionResultsEvent = ({
    communityId,
    communityType,
    communityUserRole,
    hasAccessToTheCommunity = false,
    parentEntityId,
    resultCount = 0,
    searchedString,
    componentName,
    componentSource,
    result,
    statusCode,
    failureDescription = FailureDescription.ERROR_WHILE_FETCHING_DATA
  }: SendMentionResultsEventParams) => {
    const statusCodeValue = result === TryResult.SUCCESSFUL ? 200 : statusCode
    const failureDescriptionValue =
      result === TryResult.SUCCESSFUL
        ? FailureDescription.NOT_FAILURE_RESULT_EVENT
        : failureDescription

    dispatchDatahubDefaultEvents({
      action: Action.TRY_GET_MENTION_RESULTS,
      entity: Entity.COMMUNITY,
      event: {
        communityId,
        communityType,
        communityUserRole: communityUserRole || CommunityMemberPermissionsGroup.FOLLOWER,
        hasAccessToTheCommunity,
        result,
        statusCode: statusCodeValue || 500,
        failureDescription: failureDescriptionValue,
        componentName,
        componentSource,
        searchedString,
        resultCount,
        parentEntityId,
        arrivedFrom: newFeedEnabled ? ArrivedFrom.COMMUNITY_FEED_V3 : ArrivedFrom.COMMUNITY_NEW_FEED
      }
    })

    optimizely.track(Action.TRY_GET_MENTION_RESULTS)
  }

  const sendChosenMentionEvent = ({
    communityId,
    communityType,
    communityUserRole,
    hasAccessToTheCommunity = false,
    componentName,
    mentionType,
    mentionId,
    searchedString,
    chosenStringHeadline,
    chosenStringSubtitle,
    parentEntityId
  }: SendChosenMentionEventParams) => {
    dispatchDatahubDefaultEvents({
      action: Action.CLICK_CHOSEN_MENTION,
      entity: Entity.COMMUNITY,
      event: {
        communityId,
        communityType,
        hasAccessToTheCommunity,
        componentName,
        mentionType,
        mentionId,
        searchedString,
        chosenStringHeadline,
        chosenStringSubtitle,
        parentEntityId,
        communityUserRole: communityUserRole || CommunityMemberPermissionsGroup.FOLLOWER,
        arrivedFrom: newFeedEnabled ? ArrivedFrom.COMMUNITY_FEED_V3 : ArrivedFrom.COMMUNITY_NEW_FEED
      }
    })

    optimizely.track(Action.CLICK_CHOSEN_MENTION)
  }

  const sendPublishContentMentionEvent = ({
    communityId,
    communityType,
    communityUserRole,
    hasAccessToTheCommunity = false,
    componentName,
    componentSource,
    userMentionCount,
    classMentionCount,
    parentEntityId,
    createdEntityId,
    result,
    statusCode,
    failureDescription = FailureDescription.ERROR_WHILE_FETCHING_DATA
  }: SendPublishContentMentionEventParams) => {
    if (userMentionCount) {
      const statusCodeValue = result === TryResult.SUCCESSFUL ? 200 : statusCode
      const failureDescriptionValue =
        result === TryResult.SUCCESSFUL
          ? FailureDescription.NOT_FAILURE_RESULT_EVENT
          : failureDescription

      dispatchDatahubDefaultEvents({
        action: Action.TRY_PUBLISH_CONTENT_MENTION,
        entity: Entity.COMMUNITY,
        event: {
          communityId,
          communityType,
          hasAccessToTheCommunity,
          communityUserRole: communityUserRole || CommunityMemberPermissionsGroup.FOLLOWER,
          result,
          statusCode: statusCodeValue || 500,
          failureDescription: failureDescriptionValue,
          componentName,
          componentSource,
          userMentionCount,
          classMentionCount,
          parentEntityId,
          createdEntityId,
          arrivedFrom: newFeedEnabled
            ? ArrivedFrom.COMMUNITY_FEED_V3
            : ArrivedFrom.COMMUNITY_NEW_FEED
        }
      })

      optimizely.track(Action.TRY_PUBLISH_CONTENT_MENTION)
    }
  }

  return {
    sendMentionResultsEvent,
    sendChosenMentionEvent,
    sendPublishContentMentionEvent
  }
}

export default useMentionTrackEvents
